import { forwardRef, useRef, MutableRefObject, useCallback, useLayoutEffect, useEffect } from 'react';
import { InputTextarea, InputTextareaProps } from 'primereact/inputtextarea';
import styled from 'styled-components';

/*
 * A wrapper around PrimeReact's InputTextarea that limits
 * the component's growth to a specified number of rows (maxRows).
 * Automatically manages vertical scrolling when the content exceeds
 * the defined number of rows, while preserving PrimeReact's autoResize behavior.
 * Optionally displays a character counter when maxLength is provided.
 */

type Props = InputTextareaProps & {
  maxRows?: number;
  hasError?: boolean;
  showCounter?: boolean;
};

const TextareaWithMaxRows = forwardRef<HTMLTextAreaElement, Props>(
  ({ maxRows, hasError, showCounter = true, autoResize = true, ...rest }, ref) => {
    /* Local reference to the underlying <textarea> element */
    const localRef = useRef<HTMLTextAreaElement | null>(null);
    /*
     * Merge external ref (passed from parent) with the local one.
     * This ensures both internal logic and parent components
     * have access to the textarea DOM node.
     */
    const mergedRef = (node: HTMLTextAreaElement | null) => {
      localRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as MutableRefObject<HTMLTextAreaElement | null>).current = node;
    };

    /* Cache the computed line-height to avoid recalculating it every render */
    const lineHeightRef = useRef<number | null>(null);

    /* Reference to a ResizeObserver instance to monitor autoResize changes */
    const roRef = useRef<ResizeObserver | null>(null);

    /*
     * Core logic that applies the "max rows" constraint.
     * - Reads the real line-height using getComputedStyle().
     * - Calculates maxHeight = lineHeight * maxRows.
     * - Sets max-height accordingly and toggles vertical scroll
     *   when the content exceeds the allowed height.
     */
    const applyMaxRowsLimit = useCallback(() => {
      const el = localRef.current;
      if (!el) return;

      // No limit defined → remove any previous style overrides
      if (!maxRows) {
        el.style.maxHeight = '';
        el.style.overflowY = '';
        return;
      }

      // Measure and cache line-height once
      if (lineHeightRef.current == null) {
        const cs = getComputedStyle(el);
        const parsed = parseFloat(cs.lineHeight || '');

        // Fallback: if 'normal' or NaN, approximate as 1.4 * font-size
        lineHeightRef.current = Number.isFinite(parsed) ? parsed : Math.round(parseFloat(cs.fontSize || '14') * 1.4);
      }

      const lh = lineHeightRef.current!;
      const maxH = Math.ceil(lh * maxRows);

      // Apply max-height in pixels
      el.style.maxHeight = `${maxH}px`;

      // Check if the content exceeds the max height
      const isOverflowing = el.scrollHeight > maxH + 1; // +1px to avoid rounding artifacts

      // Enable vertical scroll only when the content overflows
      el.style.overflowY = isOverflowing ? 'auto' : 'hidden';
    }, [maxRows]);

    /*
     * useLayoutEffect:
     * Runs synchronously after all DOM mutations but before the browser paints.
     * Ensures height limits are applied immediately after value changes,
     * preventing flickering during typing.
     */
    useLayoutEffect(() => {
      applyMaxRowsLimit();
    }, [rest.value, applyMaxRowsLimit]);

    /*
     * useEffect:
     * Sets up a ResizeObserver that reacts to PrimeReact's internal
     * autoResize adjustments. Whenever the component resizes itself,
     * this callback reapplies the maxRows constraint.
     */
    useEffect(() => {
      const el = localRef.current;
      if (!el) return;

      // Apply initial max-height right after mounting
      applyMaxRowsLimit();

      // Observe future size changes (autoResize, font scaling, etc.)
      roRef.current = new ResizeObserver(() => applyMaxRowsLimit());
      roRef.current.observe(el);

      // Clean up on unmount
      return () => {
        roRef.current?.disconnect();
        roRef.current = null;
      };
    }, [applyMaxRowsLimit]);

    const shouldShowCounter = showCounter && rest.maxLength;
    const currentLength = typeof rest.value === 'string' ? rest.value.length : 0;
    const isLimitExceeded = rest.maxLength ? currentLength >= (rest.maxLength as number) : false;

    if (shouldShowCounter) {
      return (
        <TextareaWrapper>
          <StyledInputTextarea
            {...rest}
            ref={mergedRef}
            autoResize={autoResize}
            $hasError={hasError}
            $isLimitExceeded={isLimitExceeded}
          />
          <CharCounterWrapper>
            <span>Please keep your text within {rest.maxLength} characters</span>
            <span>
              {currentLength} / {rest.maxLength}
            </span>
          </CharCounterWrapper>
        </TextareaWrapper>
      );
    }

    return (
      <StyledInputTextarea
        {...rest}
        ref={mergedRef}
        autoResize={autoResize}
        $hasError={hasError}
        $isLimitExceeded={isLimitExceeded}
      />
    );
  }
);

// Set a readable name for React DevTools and error traces.
// Without this, forwardRef components appear as just <ForwardRef />.
// This makes debugging and component trees much clearer.
TextareaWithMaxRows.displayName = 'TextareaWithMaxRows';

const TextareaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CharCounterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-family: Inter;
  font-weight: 400;
  font-style: Regular;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0%;
  color: var(--Primary-Black, #151515);
`;

export const StyledInputTextarea = styled(InputTextarea)<{ $hasError?: boolean; $isLimitExceeded?: boolean }>`
  border-color: ${(props) =>
    props.$hasError ? props.theme.colors.systemStatusError : props.theme.colors.elementsInputBorder};
  border-radius: 2px;
  font-size: 14px;
  color: var(--Primary-Black, #151515);
  &::placeholder {
    color: var(--Primary-Black, #15151580);
  }
  &:enabled:focus {
    border-color: ${(p) => (p.$isLimitExceeded ? p.theme.colors.elementsInputBorder : p.theme.colors.primaryOrange)};
    box-shadow: none;
  }
  &:enabled:hover {
    border-color: ${(p) => (p.$isLimitExceeded ? p.theme.colors.elementsInputBorder : p.theme.colors.primaryOrange)};
  }
`;

export default TextareaWithMaxRows;
